import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { waitForTokenRefresh } from '@vs/oidc-client';
import { ClientError } from 'graphql-request';

import { VsGraphQLError } from './classes/VsGraphQLError';
import { isErrorOfVsGraphQL } from './errorHelper';
import { setErrorInstance } from './errorInstanceMap';
import {
  ReferenceOfMeta,
  RtkErrorResponseOfVsGraphQL,
  VsGraphQLFetchArgs,
} from './type';

type ExtraOptions = Partial<Pick<ClientError, 'request' | 'response'>> & {
  isPublicEndpoint?: boolean;
};

type DynamicGraphBaseQueryFn = BaseQueryFn<
  VsGraphQLFetchArgs,
  unknown,
  RtkErrorResponseOfVsGraphQL,
  ExtraOptions
>;

export const dynamicGraphqlBaseQuery =
  (referenceOfMeta: ReferenceOfMeta): DynamicGraphBaseQueryFn =>
  async (queryArgs, baseQueryApi, extraOptions = {}) => {
    const {
      _baseUrl,
      _getTokenFunc,
      _prepareGlobalHeaders = () => {},
    } = referenceOfMeta;

    const bs = graphqlRequestBaseQuery({
      url: _baseUrl || '',
      prepareHeaders: async (headers, api) => {
        await _prepareGlobalHeaders(headers);

        if (!extraOptions.isPublicEndpoint) {
          await waitForTokenRefresh();
          if (typeof _getTokenFunc === 'function') {
            const token = await _getTokenFunc();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
          }
        }

        return headers;
      },
      customErrors({ response }) {
        const respErrors = response.errors ?? [];
        const error: RtkErrorResponseOfVsGraphQL = {
          status: response.status,
          errors: respErrors.map(({ message, extensions }) => ({
            message,
            code: extensions?.code,
            stack: extensions?.stacktrace,
          })),
        };
        return error;
      },
    });

    const result = await Promise.resolve(
      bs(queryArgs, baseQueryApi, extraOptions)
    );

    if (result.error) {
      if (isErrorOfVsGraphQL(result.error)) {
        setErrorInstance(
          result.error,
          new VsGraphQLError(result.error, queryArgs, baseQueryApi.endpoint)
        );
      }
    }

    return result;
  };
